import { Box, Typography, IconButton } from "@mui/material";
import React, { useEffect, useState, useRef } from "react";
import LinearProgress from "@mui/material/LinearProgress";
import CloseIcon from "@mui/icons-material/Close";
import { api } from "../../utils/api";
import { getFileIcon, getFileName } from "../../utils/getFileIcon";
import axios from "axios";
import { formatTimestamp } from "../../utils/formatTimeStamp";
import uploadbutton from "../../assets/images/uploadbutton.svg";
import { getImageUrl } from "../../utils/common";

const FileField = ({
  handleChange,
  handleDelete,
  setUploadProgress,
  setUploadedFiles,
  title,
  name,
  setErrors,
  errors,
  fileUrls,
  progress,
  subText,
  clearFile,
}) => {
  console.log("first999", clearFile);
  const [isDragOver, setIsDragOver] = useState(false);
  const [uploadedProgress, setUploadedProgress] = useState(0);
  const [signedUrls, setSignedUrls] = useState([]);
  const prevClearFileRef = useRef(clearFile);
  const prevFileUrlsRef = useRef([]);

  // Filter image files
  const imageFiles = fileUrls.filter((url) => {
    const fileExtension = url.split(".").pop().toLowerCase();
    return ["png", "jpg", "jpeg", "gif", "webp"].includes(fileExtension);
  });

  // Filter document files
  const documentFiles = fileUrls.filter((url) => {
    const fileExtension = url.split(".").pop().toLowerCase();
    return !["png", "jpg", "jpeg", "gif", "webp"].includes(fileExtension);
  });

  // Handle clearFile prop to reset state
  useEffect(() => {
    if (clearFile && !prevClearFileRef.current) {
      setSignedUrls([]);
      setUploadedProgress(0);
    }
    prevClearFileRef.current = clearFile;
  }, [clearFile]);

  // Fetch signed URLs for images - FIXED: Prevent infinite loop
  useEffect(() => {
    // Only fetch if imageFiles actually changed
    const imageFilesChanged = JSON.stringify(imageFiles) !== JSON.stringify(prevFileUrlsRef.current);
    
    if (!imageFilesChanged) return;

    const fetchSignedUrls = async () => {
      if (imageFiles.length === 0) {
        setSignedUrls([]);
        prevFileUrlsRef.current = imageFiles;
        return;
      }

      try {
        const urls = await Promise.all(
          imageFiles.map((fileUrl) => getImageUrl(fileUrl))
        );
        setSignedUrls(urls);
        prevFileUrlsRef.current = imageFiles;
      } catch (error) {
        console.error("Error fetching signed URLs:", error);
        setSignedUrls([]);
        prevFileUrlsRef.current = imageFiles;
      }
    };

    fetchSignedUrls();
  }, [imageFiles]); 

  const handleFileChange = async (e) => {
    const { name, files } = e.target;

    setErrors((prevErrors) => ({ ...prevErrors, [name]: [] }));

    if (!files || files.length === 0) return;

    if (fileUrls.length + files.length > 5) {
      setErrors((prevErrors) => ({
        ...prevErrors,
        [name]: ["You cannot upload more than 5 files."],
      }));
      return;
    }

    const newErrors = [];
    const uploaded = [];
    const progressTracker = Array(files.length).fill(0);

    const excludedTypes = [
      "application/zip",
      "application/x-rar-compressed",
      "application/x-tar",
      "application/gzip",
      "application/x-7z-compressed",
      "application/x-msdownload",
      "application/x-msdos-program",
    ];

    const excludedExtensions = [".zip", ".rar", ".tar", ".gz", ".7z", ".exe"];
    const maxFileSize = 5 * 1024 * 1024; // 5 MB

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const fileExtension1 = file.name.toLowerCase().split(".").pop();

      // Validation for file type and size
      if (
        excludedTypes.includes(file.type) ||
        excludedExtensions.includes(`.${fileExtension1}`)
      ) {
        newErrors.push(`File type not allowed: ${file.name}`);
        continue;
      }

      if (file.size > maxFileSize) {
        newErrors.push(`File size exceeds 5MB: ${file.name}`);
        continue;
      }

      // Generate unique file name
      const timestamp = Date.now();
      const formattedTimestamp = formatTimestamp(timestamp);
      const originalFileName = file.name.split(".").slice(0, -1).join(".");
      const fileExtension = file.name.split(".").pop();
      const uniqueFileName = `${originalFileName}_${formattedTimestamp}.${fileExtension}`;
      const uniqueFile = new File([file], uniqueFileName, { type: file.type });

      const formData = new FormData();
      formData.append("files", uniqueFile);

      try {
        // Upload the file and track progress
        const res = await axios.post(`${api}/cloud/upload`, formData, {
          onUploadProgress: (progressEvent) => {
            const percent = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            progressTracker[i] = percent;

            // Update progress bar dynamically
            const overallProgress = Math.round(
              progressTracker.reduce((sum, p) => sum + p, 0) / files.length
            );
            setUploadedProgress(overallProgress);
          },
        });
        uploaded.push(...res.data.urls);
      } catch (error) {
        newErrors.push(`Failed to upload: ${file.name}`);
      }
    }

    // Reset progress and update files
    setUploadedFiles((prev) => [...prev, ...uploaded]);
    setUploadedProgress(0);

    if (newErrors.length > 0) {
      setErrors((prevErrors) => ({ ...prevErrors, [name]: newErrors }));
    }

    e.target.value = null;
  };

  // Handle file deletion
  const handleDeleteFile = (fileUrl) => {
    
    // Remove from uploaded files
    setUploadedFiles((prev) => prev.filter((file) => file !== fileUrl));
    
    // If it's an image, also remove from signedUrls
    const fileExtension = fileUrl.split(".").pop().toLowerCase();
    if (["png", "jpg", "jpeg", "gif", "webp"].includes(fileExtension)) {
      setSignedUrls((prev) => {
        const fileIndex = fileUrls.indexOf(fileUrl);
        if (fileIndex !== -1) {
          return prev.filter((_, index) => index !== fileIndex);
        }
        return prev;
      });
    }
    
    setErrors((prevErrors) => ({ ...prevErrors, [name]: "" }));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setUploadedProgress(0);
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleChange
        ? handleChange({ target: { name, files } })
        : handleFileChange({ target: { name, files } });
    }
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1, mt: 2 }}>
        {title}
      </Typography>

      {/* Display images */}
      {signedUrls.length > 0 && (
        <Box mb={2} sx={{ width: "100%" }}>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {signedUrls.map((fileUrl, index) => {
              const originalFileUrl = imageFiles[index];
              
              return (
                <Box
                  key={`${originalFileUrl}-${index}`}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#f9f9f9",
                    width: "100px",
                    height: "80px",
                    overflow: "hidden",
                  }}
                >
                  <img
                    src={fileUrl}
                    alt={`Document ${index + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      borderRadius: "4px",
                    }}
                    onError={(e) => {
                      e.target.src = "https://via.placeholder.com/100x80?text=Error";
                    }}
                  />
                  <IconButton
                    onClick={() =>
                      handleDelete
                        ? handleDelete(name, originalFileUrl)
                        : handleDeleteFile(originalFileUrl)
                    }
                    sx={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      color: "white",
                      backgroundColor: "rgba(0,0,0,0.5)",
                      width: "20px",
                      height: "20px",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.7)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "12px" }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {/* Display documents */}
      {documentFiles.length > 0 && (
        <Box mb={2} sx={{ width: "100%" }}>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {documentFiles.map((fileUrl, index) => {
              const fileExtension = fileUrl.split(".").pop();
              const fileName = getFileName(fileUrl);

              return (
                <Box
                  key={`${fileUrl}-${index}`}
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    backgroundColor: "#f9f9f9",
                    width: "45%",
                    minWidth: "100px",
                    overflowX: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      padding: 1,
                      width: "100%",
                    }}
                  >
                    {getFileIcon(fileExtension)}
                    <Typography
                      variant="caption"
                      noWrap
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        ml: 1,
                      }}
                    >
                      {fileName}
                    </Typography>
                  </Box>

                  <IconButton
                    onClick={() =>
                      handleDelete
                        ? handleDelete(name, fileUrl)
                        : handleDeleteFile(fileUrl)
                    }
                    sx={{
                      position: "absolute",
                      top: "2px",
                      right: "2px",
                      color: "white",
                      backgroundColor: "rgba(0,0,0,0.5)",
                      width: "20px",
                      height: "20px",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.7)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: "12px" }} />
                  </IconButton>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {(progress > 0 || uploadedProgress > 0) && (
        <Box sx={{ mt: 1, mb: 1, width: "100%" }}>
          <LinearProgress variant="determinate" value={uploadedProgress || progress} sx={{ width: "100%" }} />
        </Box>
      )}

      {/* Drag & Drop upload */}
      <Box
        style={{
          border: "2px solid rgba(28, 72, 107, 1)",
          borderRadius: "6px",
          padding: "1rem",
          textAlign: "center",
          cursor: "pointer",
          width: "100%",
          backgroundColor: isDragOver ? "#f1f1f1" : "#fff",
          borderStyle: isDragOver ? "dashed" : "solid",
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input
          type="file"
          hidden
          id={`upload-document-${name}`}
          name={name}
          onChange={handleChange ? handleChange : handleFileChange}
          multiple
          accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.webp"
        />
        <label htmlFor={`upload-document-${name}`} style={{ cursor: "pointer" }}>
          <Box display="flex" flexDirection="column" alignItems="center">
            <img src={uploadbutton} alt="Upload Icon" />
            <p
              style={{
                fontFamily: "Inter",
                fontSize: "16px",
                fontWeight: "500",
                marginTop: "10px",
              }}
            >
              Drop files here or click to upload
            </p>
            <p
              style={{
                fontFamily: "Inter",
                fontSize: "12px",
                fontWeight: "400",
                textAlign: "center",
                marginTop: "-10px",
                marginBottom: "-0.5rem",
              }}
            >
              {subText ??
                "Upload your file (CSV, Excel, PNG, JPG, max 5 MB, recommended size 1200x800 pixels)."}
            </p>
          </Box>
        </label>
      </Box>

      {errors && Array.isArray(errors) ? (
        errors.map((error, index) => (
          <Typography key={index} variant="body2" color="#D34040">
            {error}
          </Typography>
        ))
      ) : errors ? (
        <Typography variant="body2" color="#D34040">
          {errors}
        </Typography>
      ) : null}
    </Box>
  );
};

export default FileField;